import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { getPaymentMethodsForCountry } from '@/constants/paymentMethods'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { useDynamicSupportedRoutes } from '@/hooks/useCpn'
import { useMaintenanceStatus } from '@/hooks/useMaintenance'
import { authService } from '@/services/auth'
import { useSendLimitsStore } from '@/stores/sendLimitsStore'
import { TransferData } from '@/types/payment'
import { CpnPaymentMethod, getPaymentMethodInfo } from '@/utils/cpn'
import {
  calculateExchangeRate,
  formatNumberWithCommas,
} from '@/utils/currencyUtils'
import {
  type CpnErrorWithResponse,
  getAmountOutOfRangeErrorData,
} from '@/utils/errorFormatting'
import { logger } from '@/utils/logger'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import {
  ArrowLeft,
  ChevronDown,
  CreditCard,
  Info,
  LandmarkIcon,
} from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'

import QuoteStatus from '../common/QuoteStatus'
import CurrencySelector from '../overlays/CurrencySelector'
import PaymentMethodSelector from '../overlays/PaymentMethodSelector'

interface SendMoneyScreenProps {
  transferData: TransferData
  setTransferData: (data: TransferData) => void
  setActiveField: (field: 'send' | 'receive' | null) => void
  onNext: () => void
  onInitiateTransfer: () => void
  onBack: () => void
  onShowAuth: () => void
  isQuoteLoading: boolean
  quoteError?: Error | null
  quoteMode?: 'fromExact' | 'toExact' | 'none'
  formattedQuote: {
    sourceAmount: number
    destinationAmount: number
    exchangeRate: number
    cadUsdcRate: number
    cpnExchangeRate: number
    fees: { amount: number; currency: string; percentage: number }
    expiresAt: Date
    paymentMethod: string
    recipientLimits?: { min: number; max: number; currency: string }
  }
}

const SendMoneyScreen = ({
  transferData,
  setTransferData,
  setActiveField,
  onNext,
  onInitiateTransfer,
  onBack,
  isQuoteLoading = false,
  quoteError,
  quoteMode,
  formattedQuote,
}: SendMoneyScreenProps) => {
  const [quoteLocked, setQuoteLocked] = useState(false)
  const [receiveCurrencyFlag, setReceiveCurrencyFlag] = useState('🏳️')
  const [isSendAmountFocused, setIsSendAmountFocused] = useState(false)
  const [showCurrencySelector, setShowCurrencySelector] = useState(false)
  const [isReceiveAmountFocused, setIsReceiveAmountFocused] = useState(false)
  const [timeUntilExpiry, setTimeUntilExpiry] = useState<number | null>(null)
  const [showPaymentMethodSelector, setShowPaymentMethodSelector] =
    useState(false)
  const [autoCorrectedField, setAutoCorrectedField] = useState<
    'send' | 'receive' | null
  >(null)

  const { data: CPN_SUPPORTED_ROUTES } = useDynamicSupportedRoutes()
  const { isMaintenanceActive } = useMaintenanceStatus('atb')
  const { isTesting } = useFeatureFlags()
  const { minSendAmount, maxSendAmount, setLimits } = useSendLimitsStore()

  const getCountryRoute = useCallback(
    (countryCode: string) => {
      return CPN_SUPPORTED_ROUTES?.[countryCode]
    },
    [CPN_SUPPORTED_ROUTES]
  )

  const getCurrencyConfig = useCallback(
    (countryCode: string, currencyCode: string) => {
      const route = getCountryRoute(countryCode)
      return route?.currencies.find(
        (currency) => currency.code === currencyCode
      )
    },
    [getCountryRoute]
  )

  // Update limits when destination currency changes
  useEffect(() => {
    const currencyConfig = getCurrencyConfig(
      transferData.receiveCountry,
      transferData.receiveCurrency
    )

    if (currencyConfig?.amountLimits) {
      setLimits(
        currencyConfig.amountLimits.min,
        currencyConfig.amountLimits.max
      )
    }
  }, [
    transferData.receiveCountry,
    transferData.receiveCurrency,
    getCurrencyConfig,
    setLimits,
  ])
  useEffect(() => {
    const err = quoteError as CpnErrorWithResponse | undefined
    if (
      err?.response?.data?.code === 'AMOUNT_OUT_OF_RANGE' &&
      err.response.data.limits
    ) {
      const { min, max } = err.response.data.limits
      setLimits(min, max)
    }
  }, [quoteError, setLimits])

  const getAvailablePaymentMethods = (
    countryCode: string,
    currencyCode: string
  ): CpnPaymentMethod[] => {
    const currencyConfig = getCurrencyConfig(countryCode, currencyCode)
    const apiPaymentMethods = currencyConfig?.paymentMethods || []

    const allowedMethods = getPaymentMethodsForCountry(countryCode, isTesting)

    // If no allowed methods configured for this country, return empty array
    if (allowedMethods.length === 0) {
      return []
    }

    // Return intersection of API methods and allowed methods
    return apiPaymentMethods.filter((method) => allowedMethods.includes(method))
  }

  const getDefaultPaymentMethod = (
    countryCode: string,
    currencyCode: string
  ): CpnPaymentMethod | undefined => {
    const allowedMethods = getAvailablePaymentMethods(countryCode, currencyCode)

    // Return the first allowed method (since we've already filtered by country rules)
    return allowedMethods.length > 0 ? allowedMethods[0] : undefined
  }

  // Parse and validate locked quote data
  const getLockedQuoteData = () => {
    try {
      const lockedQuoteData = sessionStorage.getItem('lockedQuoteData')
      if (!lockedQuoteData) return null

      const quoteData = JSON.parse(lockedQuoteData)
      return quoteData
    } catch (error) {
      logger.error('Failed to parse locked quote data', error)
      // Clear corrupted data
      sessionStorage.removeItem('lockedQuoteData')
      sessionStorage.removeItem('currentQuoteId')
      return null
    }
  }

  // Check quote expiration and update state
  const checkQuoteExpiration = () => {
    const quoteData = getLockedQuoteData()
    if (!quoteData) {
      setQuoteLocked(false)
      setTimeUntilExpiry(null)
      return
    }

    // Check if we have a valid quote with expiration
    const quote = quoteData.quotes?.[0]
    if (!quote?.quoteExpireDate) {
      setQuoteLocked(false)
      setTimeUntilExpiry(null)
      return
    }

    const now = new Date()
    const expiry = new Date(quote.quoteExpireDate)
    const timeLeft = expiry.getTime() - now.getTime()

    if (timeLeft <= 0) {
      // Quote has expired
      setQuoteLocked(false)
      setTimeUntilExpiry(0)
      // Auto-clear expired quote
      clearExpiredQuote()
    } else {
      // Quote is still valid
      setQuoteLocked(true)
      setTimeUntilExpiry(Math.floor(timeLeft / 1000)) // Convert to seconds
    }
  }

  // Clear expired or invalid quote data
  const clearExpiredQuote = () => {
    sessionStorage.removeItem('lockedQuoteData')
    sessionStorage.removeItem('currentQuoteId')
  }

  // Set up timer for quote expiration countdown
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>

    if (quoteLocked && timeUntilExpiry !== null && timeUntilExpiry > 0) {
      interval = setInterval(() => {
        setTimeUntilExpiry((prev) => {
          if (prev === null || prev <= 1) {
            checkQuoteExpiration() // Re-check expiration
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [quoteLocked, timeUntilExpiry])

  // Check expiration periodically (every 30 seconds)
  useEffect(() => {
    const periodicCheck = setInterval(() => {
      if (quoteLocked || sessionStorage.getItem('lockedQuoteData')) {
        checkQuoteExpiration()
      }
    }, 30000)

    return () => clearInterval(periodicCheck)
  }, [quoteLocked])

  // Update flag when dynamic routes data becomes available
  useEffect(() => {
    if (CPN_SUPPORTED_ROUTES && transferData.receiveCountry) {
      const currentFlag =
        getCountryRoute(transferData.receiveCountry)?.flag || '🏳️'
      setReceiveCurrencyFlag(currentFlag)
    }
  }, [CPN_SUPPORTED_ROUTES, transferData.receiveCountry, getCountryRoute])

  const handleCurrencySelect = (
    currency: string,
    flag: string,
    countryCode: string,
    paymentMethod?: string
  ) => {
    const selectedPaymentMethod =
      paymentMethod || getDefaultPaymentMethod(countryCode, currency)

    setTransferData({
      ...transferData,
      receiveCurrency: currency,
      receiveCountry: countryCode,
      paymentMethod: selectedPaymentMethod as CpnPaymentMethod,
    })
    setReceiveCurrencyFlag(flag)
    setShowCurrencySelector(false)
  }

  const handleSendAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAutoCorrectedField((prev) => (prev === 'send' ? null : prev))
    let value = e.target.value

    // Allow only numbers and decimal point (no commas while typing)
    if (/^\d*\.?\d{0,2}$/.test(value) || value === '') {
      // Prevent leading zeros (except for "0." case)
      if (value.length > 1 && value.startsWith('0') && value[1] !== '.') {
        value = value.replace(/^0+/, '')
      }

      if (value !== '') {
        const numValue = parseFloat(value)
        if (!isNaN(numValue) && numValue > maxSendAmount) {
          value =
            maxSendAmount % 1 === 0
              ? String(maxSendAmount)
              : maxSendAmount.toFixed(2)
          setAutoCorrectedField('send')
        }
      }

      setActiveField?.('send')
      setTransferData({
        ...transferData,
        sendAmount: value,
      })
    }
  }

  const handleReceiveAmountChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAutoCorrectedField((prev) => (prev === 'receive' ? null : prev))
    let value = e.target.value

    // Allow only numbers and decimal point (no commas while typing)
    if (/^\d*\.?\d{0,2}$/.test(value) || value === '') {
      // Prevent leading zeros (except for "0." case)
      if (value.length > 1 && value.startsWith('0') && value[1] !== '.') {
        value = value.replace(/^0+/, '')
      }

      if (value !== '') {
        const numValue = parseFloat(value)
        if (
          !isNaN(numValue) &&
          hasRecipientLimits &&
          maxRecipientAmount > 0 &&
          numValue > maxRecipientAmount
        ) {
          value =
            maxRecipientAmount % 1 === 0
              ? String(maxRecipientAmount)
              : maxRecipientAmount.toFixed(2)
          setAutoCorrectedField('receive')
        }
      }

      const numValue = parseFloat(value)
      if (isNaN(numValue) || value === '' || numValue >= 0) {
        setActiveField?.('receive')
        setTransferData({
          ...transferData,
          receiveAmount: value,
        })
      }
    }
  }

  const handleReceiveAmountBlur = () => {
    // Format the receive amount when user finishes editing
    const cleanValue = transferData.receiveAmount.trim()
    const numValue = parseFloat(cleanValue)
    if (!isNaN(numValue) && numValue > 0) {
      setTransferData({
        ...transferData,
        receiveAmount: numValue.toFixed(2),
      })
    } else if (cleanValue === '' || numValue <= 0) {
      setTransferData({
        ...transferData,
        receiveAmount: '1000.00',
      })
    }
  }

  const handlePaymentMethodSelect = (paymentMethod: CpnPaymentMethod) => {
    setTransferData({
      ...transferData,
      paymentMethod,
    })
  }

  // Get available payment methods for current destination country and currency
  const availablePaymentMethods = getAvailablePaymentMethods(
    transferData.receiveCountry,
    transferData.receiveCurrency
  )

  // Calculate fee percentage - use formatted quote data if available
  const displayFees = formattedQuote?.fees || {
    amount: transferData.fees,
    percentage: 0,
  }

  // Calculate exchange rate using API rates for accurate rate
  const calculatedExchangeRate = calculateExchangeRate({
    sendAmount: transferData.sendAmount,
    destinationAmount: formattedQuote?.destinationAmount?.toString() || '0',
    fallbackRate: formattedQuote?.exchangeRate || transferData.exchangeRate,
    cadUsdcRate: formattedQuote?.cadUsdcRate,
    cpnExchangeRate: formattedQuote?.cpnExchangeRate,
  })

  const rateFromTransfer =
    transferData.exchangeRate && transferData.exchangeRate !== 1.0
      ? transferData.exchangeRate
      : 0
  const exchangeRate = formattedQuote?.exchangeRate ?? rateFromTransfer ?? 0

  const {
    isAmountOutOfRangeError,
    minRecipientAmount,
    maxRecipientAmount,
    recipientLimitCurrency,
    hasRecipientLimits,
  } = getAmountOutOfRangeErrorData({
    quoteError,
    exchangeRate,
    minSendAmount,
    maxSendAmount,
    receiveCurrency: transferData.receiveCurrency,
    recipientLimitsFromSuccess: formattedQuote?.recipientLimits ?? null,
  })

  const receiveAmountDisplay = (() => {
    if (isReceiveAmountFocused) {
      return transferData.receiveAmount ?? ''
    }
    const amount = transferData.receiveAmount
    return amount ? formatNumberWithCommas(amount) : ''
  })()

  return (
    <TooltipProvider>
      <div className="flex flex-col h-[600px] dark:bg-gray-900">
        <div className="p-4 space-y-2 flex-1 flex flex-col dark:bg-gray-900">
          {/* Header */}
          <div className="relative text-center space-y-1">
            {/* Back button for authenticated users */}
            {onBack && authService.isAuthenticated() && (
              <button
                onClick={onBack}
                className="absolute left-0 top-0 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            )}
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              Send Money Worldwide
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Fast, secure, and affordable international transfers
            </p>
            {isAmountOutOfRangeError && !autoCorrectedField && (
              <div className="mt-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md space-y-1">
                {quoteMode === 'toExact' ? (
                  hasRecipientLimits && (
                    <p className="text-xs text-red-700 dark:text-red-300">
                      {transferData.receiveAmount
                        ? `Amount ${formatNumberWithCommas(transferData.receiveAmount)} ${recipientLimitCurrency} is outside the allowed range.`
                        : ''}
                      <br />
                      <br />
                      Recipient can receive{' '}
                      {formatNumberWithCommas(
                        String(minRecipientAmount)
                      )} – {formatNumberWithCommas(String(maxRecipientAmount))}{' '}
                      {recipientLimitCurrency}.
                    </p>
                  )
                ) : (
                  <p className="text-xs text-red-700 dark:text-red-300">
                    {transferData.sendAmount
                      ? `Amount $${formatNumberWithCommas(transferData.sendAmount)} CAD is outside the allowed range.`
                      : ''}
                    <br />
                    You can send ${minSendAmount} – $
                    {maxSendAmount.toLocaleString()} CAD.
                  </p>
                )}
              </div>
            )}
            {autoCorrectedField === 'send' && (
              <div className="mt-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Amount adjusted to the maximum allowed.
                  <br />
                  You can send ${minSendAmount} – $
                  {maxSendAmount.toLocaleString()} CAD.
                </p>
              </div>
            )}
            {autoCorrectedField === 'receive' && hasRecipientLimits && (
              <div className="mt-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Amount adjusted to the maximum allowed.
                  <br />
                  Recipient can receive{' '}
                  {formatNumberWithCommas(String(minRecipientAmount))} –{' '}
                  {formatNumberWithCommas(String(maxRecipientAmount))}{' '}
                  {recipientLimitCurrency}.
                </p>
              </div>
            )}
          </div>

          {/* Exchange Rate / Quote Status */}
          {!isMaintenanceActive && (
            <div className="flex justify-center">
              <Card
                className={`px-3 py-2 inline-block ${
                  quoteLocked
                    ? 'bg-green-50 border-green-200 dark:bg-green-900 dark:border-green-700'
                    : isQuoteLoading
                      ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900 dark:border-yellow-700'
                      : isAmountOutOfRangeError && !autoCorrectedField
                        ? 'bg-blue-50 border-blue-200 dark:bg-blue-900 dark:border-blue-700'
                        : quoteError
                          ? 'bg-red-50 border-red-200 dark:bg-red-900 dark:border-red-700'
                          : 'bg-blue-50 border-blue-200 dark:bg-blue-900 dark:border-blue-700'
                }`}
              >
                <QuoteStatus
                  isQuoteLoading={isQuoteLoading}
                  quoteError={quoteError}
                  exchangeRate={calculatedExchangeRate}
                  sendCurrency={transferData.sendCurrency}
                  receiveCurrency={transferData.receiveCurrency}
                />
              </Card>
            </div>
          )}
          <div className="flex-1 space-y-2">
            {/* You Send */}
            <Card className="p-2 dark:bg-gray-800 dark:border-gray-700">
              <div className="space-y-1">
                <h3 className="text-gray-700 font-medium text-sm dark:text-gray-300">
                  You send exactly
                </h3>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 px-2 py-1 rounded-lg ">
                    <span className="text-sm">🇨🇦</span>
                    <span className="font-medium text-sm">
                      {transferData.sendCurrency}
                    </span>
                  </div>
                  <div className="flex-1 ml-4">
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={
                        isSendAmountFocused
                          ? transferData.sendAmount || ''
                          : transferData.sendAmount
                            ? formatNumberWithCommas(transferData.sendAmount)
                            : ''
                      }
                      onChange={handleSendAmountChange}
                      onFocus={() => {
                        setIsSendAmountFocused(true)
                      }}
                      onBlur={() => {
                        setIsSendAmountFocused(false)
                        setActiveField?.(null)
                        // Clean up trailing decimal or ensure proper formatting
                        const cleanValue = transferData.sendAmount.trim()
                        if (cleanValue && cleanValue !== '') {
                          const numValue = parseFloat(cleanValue)
                          if (!isNaN(numValue) && numValue > 0) {
                            setTransferData({
                              ...transferData,
                              sendAmount: numValue.toFixed(2),
                            })
                          }
                        }
                      }}
                      placeholder="0.00"
                      className="text-xl font-bold text-gray-900 dark:text-white text-right border-none shadow-none p-0 h-auto bg-transparent focus-visible:ring-0 focus:outline-none"
                      style={{ fontSize: '1.25rem', fontWeight: 'bold' }}
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Recipient Gets */}
            <Card className="p-2 dark:bg-gray-800 dark:border-gray-700">
              <div className="space-y-1">
                <h3 className="text-gray-700 font-medium text-sm dark:text-gray-300">
                  Recipient gets
                </h3>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setShowCurrencySelector(true)}
                    className="flex items-center space-x-2 bg-yellow-100 bg-opacity-30 px-2 py-1 rounded-lg border-2 border-yellow-200 border-opacity-30 hover:bg-yellow-50 hover:bg-opacity-30 dark:bg-yellow-900 dark:bg-opacity-30 dark:border-yellow-700 dark:hover:bg-yellow-800 transition-colors"
                  >
                    <span className="text-sm">{receiveCurrencyFlag}</span>
                    <span className="font-medium text-sm">
                      {transferData.receiveCurrency}
                    </span>
                    <ChevronDown className="w-4 h-4 dark:text-gray-300" />
                  </button>
                  <div className="flex-1 ml-4">
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={receiveAmountDisplay}
                      onChange={handleReceiveAmountChange}
                      onFocus={() => {
                        setIsReceiveAmountFocused(true)
                      }}
                      onBlur={() => {
                        setIsReceiveAmountFocused(false)
                        handleReceiveAmountBlur()
                        setActiveField?.(null)
                      }}
                      placeholder="0.00"
                      className="text-xl font-bold text-gray-900 dark:text-white text-right border-none shadow-none p-0 h-auto bg-transparent focus-visible:ring-0 focus:outline-none"
                      style={{ fontSize: '1.25rem', fontWeight: 'bold' }}
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Payment Method Selector */}
            <Card
              className={`p-2 dark:bg-gray-800 dark:border-gray-700 ${
                availablePaymentMethods.length > 1
                  ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
                  : 'cursor-default'
              }`}
              onClick={
                availablePaymentMethods.length > 1
                  ? () => setShowPaymentMethodSelector(true)
                  : undefined
              }
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 b rounded-lg flex items-center justify-center ">
                    {getPaymentMethodInfo(transferData.paymentMethod).logo ? (
                      <img
                        src={
                          getPaymentMethodInfo(transferData.paymentMethod).logo
                        }
                        alt={
                          getPaymentMethodInfo(transferData.paymentMethod).name
                        }
                        className="w-8 h-8 object-contain rounded-md"
                      />
                    ) : (
                      <CreditCard className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">
                      Processed with{' '}
                      {getPaymentMethodInfo(transferData.paymentMethod).name}
                    </p>
                    {availablePaymentMethods.length > 1 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {availablePaymentMethods.length} methods available
                      </p>
                    )}
                  </div>
                </div>
                {availablePaymentMethods.length > 1 && (
                  <div className="flex items-center space-x-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium cursor-pointer transition-colors">
                    <span>Change</span>
                    <ChevronDown className="w-3 h-3" />
                  </div>
                )}
              </div>
            </Card>

            {/* Fees - Only show if fees are loaded and greater than 0 */}
            {displayFees.amount > 0 && displayFees.percentage > 0 && (
              <div className="border rounded-lg dark:border-gray-700 dark:bg-gray-800">
                {/* Main fees header - clickable */}
                <div className="flex items-center justify-between p-1 pl-3 pr-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 rounded-lg flex items-center justify-center">
                      <LandmarkIcon />
                    </div>
                    <div className="flex items-center space-x-2">
                      <p className="font-medium text-gray-900 dark:text-white text-sm">
                        Total fees
                      </p>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        ({displayFees.percentage.toFixed(1)}%)
                      </span>

                      <TooltipPrimitive.Root>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[200px] bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900 border border-gray-200 dark:border-gray-700">
                          <p className="text-xs">
                            Fees are inclusive. The value in "Recipient gets"
                            field is the final amount the recipient is expected
                            to receive.
                          </p>
                        </TooltipContent>
                      </TooltipPrimitive.Root>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="font-bold text-sm dark:text-white">
                      {displayFees.amount.toFixed(2)} CAD
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Delivery Time */}
            {/* {formattedQuote?.paymentMethod != "WIRE" && <p className="text-sm text-gray-600 dark:text-gray-400">Arrives within 1 Hour of Payment</p>} */}
          </div>

          {/* Initiate Transfer Button */}
          <div className="pt-1">
            <Button
              onClick={quoteLocked ? onNext : onInitiateTransfer}
              disabled={
                isMaintenanceActive ||
                !!quoteError ||
                (!quoteLocked &&
                  (!transferData.receiveAmount ||
                    transferData.receiveAmount === '0.00'))
              }
              className={`w-full text-sm font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed bg-blue-600 hover:bg-gray-800 text-white text-wrap py-4`}
            >
              {isMaintenanceActive
                ? 'GMT is temporarily unavailable. Please check back later.'
                : quoteLocked
                  ? 'Continue to Recipient →'
                  : 'Initiate Transfer'}
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3">
          <div className="text-center space-y-2">
            {/* <div className="flex items-center justify-center space-x-2">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Payment processed by
              </p>
              <img src="/interac-circle.png" alt="Interac" className="h-4" />
            </div> */}
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Secured by Paytrie • Circle Payments Network
            </p>
          </div>
        </div>

        {/* Overlays */}
        {showCurrencySelector && (
          <CurrencySelector
            onSelect={handleCurrencySelect}
            onClose={() => setShowCurrencySelector(false)}
          />
        )}

        {/* Payment Method Selector Overlay */}
        {showPaymentMethodSelector && (
          <PaymentMethodSelector
            paymentMethods={availablePaymentMethods}
            selectedPaymentMethod={
              transferData.paymentMethod as CpnPaymentMethod
            }
            currencyCode={transferData.receiveCurrency}
            countryName={
              getCountryRoute(transferData.receiveCountry)?.name ||
              transferData.receiveCountry
            }
            flag={getCountryRoute(transferData.receiveCountry)?.flag || '🏳️'}
            onSelect={handlePaymentMethodSelect}
            onClose={() => setShowPaymentMethodSelector(false)}
          />
        )}
      </div>
    </TooltipProvider>
  )
}

export default SendMoneyScreen
